import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { fetchItemsAPI } from "../../api/itemsApi"
import { toast } from "react-toastify"
import { getUserUidDisplayLabel } from "../TableHelper.tsx"
import { resolveItemCreatorUid, resolveItemOwnerUid } from "../resolveItemOwnerUid"
import { parseTableTimestamp } from "./parseTableTimestamp"
import {
  buildServerColumnFilters,
  type ColumnFilterStateInput,
  type ServerTableColumnFilters,
} from "./buildServerColumnFilters"
import { applySearchColumnFilterToInput } from "./applySearchColumnFilter"
import type { SearchColumnFilter } from "../../nav/searchNavigationState"
import { ColumnFilterTextInput } from "./ColumnFilterTextInput"

function normalizeBoolean(value: unknown): boolean | null {
  if (value === true || value === false) return value
  if (value === 1 || value === 0) return Boolean(value)
  if (typeof value === "string") {
    const v = value.trim().toLowerCase()
    if (v === "true" || v === "yes" || v === "1") return true
    if (v === "false" || v === "no" || v === "0") return false
  }
  return null
}

function getRowFormUid(r: Record<string, unknown>): string {
  const raw = r._formUid
  if (raw == null) return ""
  return String(raw).trim()
}

function buildFormFilterChoicesFromRows(
  rows: Record<string, unknown>[],
  formTitleByUid: Record<string, string> = {}
): { uid: string; label: string }[] {
  const map = new Map<string, string>()
  for (const row of rows) {
    const uid = getRowFormUid(row)
    if (!uid || map.has(uid)) continue
    const rawT = row._formTitle
    const fromRow = typeof rawT === "string" && rawT.trim() ? rawT.trim() : ""
    const fromRegistry = (formTitleByUid[uid] ?? "").trim()
    const label = fromRow || fromRegistry || uid
    map.set(uid, label)
  }
  return Array.from(map.entries())
    .map(([uid, label]) => ({ uid, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

function getSelectOptions(field: { options?: unknown }): string[] {
  const options = field?.options
  if (Array.isArray(options)) {
    return options.map((opt: unknown) => String(opt))
  }
  if (typeof options === "string") {
    return options
      .split(",")
      .map((opt: string) => opt.trim())
      .filter(Boolean)
  }
  return []
}

function formatDateFilterLabel(value: unknown): string {
  if (value == null) return ""
  const raw = String(value).trim()
  if (!raw) return ""
  const dateOnlyMatch = raw.match(/^(\d{4}-\d{2}-\d{2})$/)
  if (dateOnlyMatch) return dateOnlyMatch[1]
  const isoUtcMatch = raw.match(/^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/)
  if (!isoUtcMatch) return raw
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return isoUtcMatch[1]
  return parsed.toDateString()
}

function isNumericFieldType(rawType: unknown): boolean {
  const type = String(rawType || "").toLowerCase().trim()
  if (!type) return false

  const directHints = [
    "number",
    "int",
    "integer",
    "smallint",
    "bigint",
    "float",
    "double",
    "decimal",
    "numeric",
    "currency",
    "money",
    "price",
    "amount",
    "percent",
    "percentage",
    "qty",
    "quantity",
    "count",
    "rating",
    "score",
  ]
  if (directHints.some((hint) => type === hint || type.includes(hint))) {
    return true
  }

  const compact = type.replace(/[^a-z0-9]/g, "")
  return directHints.some((hint) => compact.includes(hint))
}

export type UseComponentTableFiltersParams = {
  currentSlug: string
  user: unknown | null
  tableData: unknown[] | undefined
  meta: unknown
  currentMeta: unknown
  setPage: Dispatch<SetStateAction<number>>
  setUpdateList: Dispatch<SetStateAction<boolean>>
  /** Public form uid → title for this collection (from form configs API). */
  formTitleByUid?: Record<string, string>
}

export type DuplicateFilterEntry = {
  fieldName: string
  comparableKey: string
}

function dateDayFromUnixSeconds(sec: number): string {
  const d = new Date(sec * 1000)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

const EMPTY_COLUMN_FILTER_INPUT: ColumnFilterStateInput = {
  text: {},
  number: {},
  boolean: {},
  select: {},
  created: {},
  updated: {},
}

/** Turn an `arr:...` duplicate key into raw filter tokens (best-effort). */
function comparableArrKeyToCsvTokens(arrKey: string): string[] {
  const inner = arrKey.startsWith("arr:") ? arrKey.slice(4) : arrKey
  if (!inner) return []
  const parts = inner.split("|")
  return parts.map((part) => {
    if (part.startsWith("uid:")) return part.slice(4)
    if (part.startsWith("s:")) return part.slice(2)
    if (part.startsWith("num:")) return part.slice(4)
    if (part.startsWith("bool:")) return part.endsWith("1") ? "true" : "false"
    if (part === "__empty__") return ""
    return part
  })
}

export function useComponentTableFilters({
  currentSlug,
  user,
  tableData,
  meta,
  currentMeta,
  setPage,
  setUpdateList,
  formTitleByUid = {},
}: UseComponentTableFiltersParams) {
  const [activeFilterColumnId, setActiveFilterColumnId] = useState<string | null>(null)
  const focusedFilterFieldRef = useRef<string | null>(null)

  useEffect(() => {
    if (!activeFilterColumnId) {
      focusedFilterFieldRef.current = null
    }
  }, [activeFilterColumnId])

  useLayoutEffect(() => {
    const field = focusedFilterFieldRef.current
    if (!field || activeFilterColumnId !== field) return
    const input = document.querySelector(
      `[data-filter-popup="${field}"] input[placeholder="Search"]`
    ) as HTMLInputElement | null
    if (!input || document.activeElement === input) return
    input.focus()
    const len = input.value.length
    input.setSelectionRange(len, len)
  })

  useEffect(() => {
    if (!activeFilterColumnId) return

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target) return

      const insidePopup = target.closest(`[data-filter-popup="${activeFilterColumnId}"]`)
      const insideTrigger = target.closest("[data-filter-trigger]")
      if (insidePopup || insideTrigger) return

      setActiveFilterColumnId(null)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        setActiveFilterColumnId(null)
      }
    }

    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [activeFilterColumnId])

  const [textFilters, setTextFilters] = useState<Record<string, string>>({})
  const [numberFilters, setNumberFilters] = useState<
    Record<string, { min?: string; max?: string; equals?: string }>
  >({})
  const [booleanFilters, setBooleanFilters] = useState<Record<string, "all" | "true" | "false">>({})
  const [selectFilters, setSelectFilters] = useState<Record<string, string[]>>({})
  const [csvFilters, setCsvFilters] = useState<Record<string, string[]>>({})
  const [csvDialogOpen, setCsvDialogOpen] = useState(false)
  const [csvDialogFieldName, setCsvDialogFieldName] = useState<string | null>(null)
  const [csvDialogInput, setCsvDialogInput] = useState("")
  const [relationFilterOptionsByField, setRelationFilterOptionsByField] = useState<Record<string, string[]>>({})
  const [relationFilterLoadingByField, setRelationFilterLoadingByField] = useState<Record<string, boolean>>({})
  const [uidColumnFilterOptionsByField, setUidColumnFilterOptionsByField] = useState<Record<string, string[]>>({})
  const [uidColumnFilterLoadingByField, setUidColumnFilterLoadingByField] = useState<Record<string, boolean>>({})
  const [formFilterOptionsByField, setFormFilterOptionsByField] = useState<
    Record<string, { uid: string; label: string }[]>
  >({})
  const [formFilterLoadingByField, setFormFilterLoadingByField] = useState<Record<string, boolean>>({})
  const [createdRange, setCreatedRange] = useState<{ from?: string; to?: string }>({})
  const [updatedRange, setUpdatedRange] = useState<{ from?: string; to?: string }>({})
  const textFiltersRef = useRef(textFilters)
  const numberFiltersRef = useRef(numberFilters)
  const booleanFiltersRef = useRef(booleanFilters)
  const selectFiltersRef = useRef(selectFilters)
  const createdRangeRef = useRef(createdRange)
  const updatedRangeRef = useRef(updatedRange)

  useEffect(() => {
    textFiltersRef.current = textFilters
  }, [textFilters])

  useEffect(() => {
    numberFiltersRef.current = numberFilters
  }, [numberFilters])

  useEffect(() => {
    booleanFiltersRef.current = booleanFilters
  }, [booleanFilters])

  useEffect(() => {
    selectFiltersRef.current = selectFilters
  }, [selectFilters])

  useEffect(() => {
    createdRangeRef.current = createdRange
  }, [createdRange])

  useEffect(() => {
    updatedRangeRef.current = updatedRange
  }, [updatedRange])

  useEffect(() => {
    setPage(1)
    setCsvFilters({})
  }, [currentSlug, setPage])

  const hasActiveFilters = useMemo(() => {
    const hasText = Object.values(textFilters).some((v) => v.trim() !== "")
    const hasNumber = Object.values(numberFilters).some(
      (v) =>
        (v.equals && v.equals.trim() !== "") ||
        (v.min && v.min.trim() !== "") ||
        (v.max && v.max.trim() !== "")
    )
    const hasBool = Object.values(booleanFilters).some((v) => v !== "all")
    const hasSelect = Object.values(selectFilters).some((arr) => Array.isArray(arr) && arr.length > 0)
    const hasCsv = Object.values(csvFilters).some((arr) => Array.isArray(arr) && arr.length > 0)
    const hasCreated = Boolean(createdRange.from || createdRange.to)
    const hasUpdated = Boolean(updatedRange.from || updatedRange.to)
    return hasText || hasNumber || hasBool || hasSelect || hasCsv || hasCreated || hasUpdated
  }, [textFilters, numberFilters, booleanFilters, selectFilters, csvFilters, createdRange, updatedRange])

  const appliedFilterCount = useMemo(() => {
    const textCount = Object.values(textFilters).filter((v) => v.trim() !== "").length
    const numberCount = Object.values(numberFilters).filter(
      (v) =>
        (v.equals && v.equals.trim() !== "") ||
        (v.min && v.min.trim() !== "") ||
        (v.max && v.max.trim() !== "")
    ).length
    const boolCount = Object.values(booleanFilters).filter((v) => v !== "all").length
    const selectCount = Object.values(selectFilters).filter((arr) => Array.isArray(arr) && arr.length > 0).length
    const csvCount = Object.values(csvFilters).filter((arr) => Array.isArray(arr) && arr.length > 0).length
    const createdCount = createdRange.from || createdRange.to ? 1 : 0
    const updatedCount = updatedRange.from || updatedRange.to ? 1 : 0
    return textCount + numberCount + boolCount + selectCount + csvCount + createdCount + updatedCount
  }, [textFilters, numberFilters, booleanFilters, selectFilters, csvFilters, createdRange, updatedRange])

  const normalizedCsvFilters = useMemo(() => {
    const out: Record<string, string[]> = {}
    Object.entries(csvFilters).forEach(([fieldName, values]) => {
      if (!Array.isArray(values)) return
      const cleaned = values.map((value) => String(value).trim()).filter(Boolean)
      if (cleaned.length > 0) {
        out[fieldName] = Array.from(new Set(cleaned))
      }
    })
    return out
  }, [csvFilters])

  const csvFilterSignature = useMemo(() => {
    const sortedEntries = Object.entries(normalizedCsvFilters)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, values]) => [key, [...values].sort()])
    return JSON.stringify(sortedEntries)
  }, [normalizedCsvFilters])

  const columnFilterInput = useMemo(
    (): ColumnFilterStateInput => ({
      text: textFilters,
      number: numberFilters,
      boolean: booleanFilters,
      select: selectFilters,
      created: createdRange,
      updated: updatedRange,
    }),
    [textFilters, numberFilters, booleanFilters, selectFilters, createdRange, updatedRange]
  )

  const [debouncedColumnFilterInput, setDebouncedColumnFilterInput] =
    useState<ColumnFilterStateInput>(columnFilterInput)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedColumnFilterInput(columnFilterInput)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [columnFilterInput])

  const serverColumnFilters = useMemo(
    (): ServerTableColumnFilters | undefined =>
      buildServerColumnFilters(debouncedColumnFilterInput),
    [debouncedColumnFilterInput]
  )

  const serverColumnFilterSignature = useMemo(
    () => JSON.stringify(serverColumnFilters ?? null),
    [serverColumnFilters]
  )

  const userUidForFilters = useMemo(() => {
    if (!user || typeof user !== "object") return ""
    const u = user as { uid?: string; id?: string; info?: { uid?: string } }
    return String(u.uid || u.id || u.info?.uid || "").trim()
  }, [user])

  const skipNextCsvFilterRefreshRef = useRef(true)
  const prevCsvFilterSlugRef = useRef(currentSlug)
  const prevCsvFilterSignatureRef = useRef<string | undefined>(undefined)
  const prevServerColumnFilterSignatureRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (prevCsvFilterSlugRef.current !== currentSlug) {
      prevCsvFilterSlugRef.current = currentSlug
      skipNextCsvFilterRefreshRef.current = true
      prevCsvFilterSignatureRef.current = undefined
      prevServerColumnFilterSignatureRef.current = undefined
    }
  }, [currentSlug])

  useEffect(() => {
    if (!currentSlug || !userUidForFilters) return

    const prev = prevCsvFilterSignatureRef.current
    if (prev === undefined) {
      prevCsvFilterSignatureRef.current = csvFilterSignature
      return
    }
    if (prev === csvFilterSignature) return

    prevCsvFilterSignatureRef.current = csvFilterSignature
    setPage(1)
    if (skipNextCsvFilterRefreshRef.current) {
      skipNextCsvFilterRefreshRef.current = false
      return
    }
    setUpdateList(true)
  }, [csvFilterSignature, currentSlug, userUidForFilters, setPage, setUpdateList])

  useEffect(() => {
    if (!currentSlug || !userUidForFilters) return

    const prev = prevServerColumnFilterSignatureRef.current
    if (prev === undefined) {
      prevServerColumnFilterSignatureRef.current = serverColumnFilterSignature
      return
    }
    if (prev === serverColumnFilterSignature) return

    prevServerColumnFilterSignatureRef.current = serverColumnFilterSignature
    setPage(1)
  }, [serverColumnFilterSignature, currentSlug, userUidForFilters, setPage])

  const isFieldFilterApplied = (field: { name?: string; type?: string }): boolean => {
    const fieldName = field?.name
    if (!fieldName) return false
    if (fieldName === "_creator" || fieldName === "_itemOwner" || fieldName === "_form") {
      const selected = selectFilters[fieldName]
      if (Array.isArray(selected) && selected.length > 0) return true
      const csvValues = csvFilters[fieldName]
      return Array.isArray(csvValues) && csvValues.length > 0
    }
    const csvValues = csvFilters[fieldName]
    if (Array.isArray(csvValues) && csvValues.length > 0) return true
    const type = String(field?.type || "").toLowerCase().trim()
    if (type === "richtext" || type === "textarea" || type === "text") {
      const term = textFiltersRef.current[fieldName]
      return Boolean(term && term.trim() !== "")
    }
    if (isNumericFieldType(type) || type === "code") {
      const range = numberFiltersRef.current[fieldName]
      return Boolean(
        range &&
        ((range.equals && range.equals.trim() !== "") ||
          (range.min && range.min.trim() !== "") ||
          (range.max && range.max.trim() !== ""))
      )
    }
    if (type === "checkbox" || type === "boolean" || type === "bool") {
      return (booleanFiltersRef.current[fieldName] ?? "all") !== "all"
    }
    if (type === "select" || type === "dropdown") {
      const selected = selectFiltersRef.current[fieldName]
      return Array.isArray(selected) && selected.length > 0
    }
    if (type === "relation" || type === "multiplerelation" || type === "systemrelation") {
      const selected = selectFiltersRef.current[fieldName]
      return Array.isArray(selected) && selected.length > 0
    }
    return false
  }

  const getRelationFilterValuesForRow = useCallback((row: Record<string, unknown>, fieldName: string): string[] => {
    const labelsKey = `${fieldName}__labels`
    const labelKey = `${fieldName}__label`
    const labelValues = row?.[labelsKey]
    const singleLabel = row?.[labelKey]

    if (Array.isArray(labelValues) && labelValues.length > 0) {
      return labelValues.map((value) => String(value).trim()).filter(Boolean)
    }

    if (singleLabel !== undefined && singleLabel !== null && String(singleLabel).trim() !== "") {
      return [String(singleLabel).trim()]
    }

    const raw = row?.[fieldName]
    if (Array.isArray(raw)) {
      return raw.map((value) => String(value).trim()).filter(Boolean)
    }
    if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
      return [String(raw).trim()]
    }
    return []
  }, [])

  const getRelationFilterOptions = useCallback(
    (fieldName: string): string[] => {
      const rows = Array.isArray(tableData) ? tableData : []
      if (!rows.length) return []

      const unique = new Set<string>()
      rows.forEach((row: Record<string, unknown>) => {
        getRelationFilterValuesForRow(row, fieldName).forEach((value) => {
          unique.add(value)
        })
      })

      return Array.from(unique).sort((a, b) => a.localeCompare(b))
    },
    [tableData, getRelationFilterValuesForRow]
  )

  const fieldTypeByName = useMemo(() => {
    const sourceFields =
      (meta as { fields?: unknown[] })?.fields || (currentMeta as { fields?: unknown[] })?.fields || []
    const out: Record<string, string> = {}
      ; (sourceFields as { name?: string; type?: string }[]).forEach((field) => {
        const name = field?.name
        if (!name) return
        out[name] = String(field?.type || "").toLowerCase().trim()
      })
    return out
  }, [meta, currentMeta])

  useEffect(() => {
    setRelationFilterOptionsByField({})
    setRelationFilterLoadingByField({})
    setUidColumnFilterOptionsByField({})
    setUidColumnFilterLoadingByField({})
    setFormFilterOptionsByField({})
    setFormFilterLoadingByField({})
  }, [currentSlug])

  useEffect(() => {
    setFormFilterOptionsByField((prev) => {
      if (!prev._form) return prev
      const next = { ...prev }
      delete next._form
      return next
    })
  }, [formTitleByUid])

  useEffect(() => {
    if (activeFilterColumnId !== "_form") return
    if (!currentSlug) return
    if (formFilterOptionsByField["_form"]) return
    if (formFilterLoadingByField["_form"]) return

    let cancelled = false
    const loadOptions = async () => {
      try {
        setFormFilterLoadingByField((prev) => ({ ...prev, _form: true }))
        const allRows: Record<string, unknown>[] = []
        let page = 1
        const limit = 100
        for (; ;) {
          const result = await fetchItemsAPI(currentSlug, { page, limit })
          const items = Array.isArray(result?.items) ? result.items : []
          allRows.push(...(items as Record<string, unknown>[]))
          if (items.length < limit) break
          page += 1
        }

        const options = buildFormFilterChoicesFromRows(allRows, formTitleByUid)
        if (!cancelled) {
          setFormFilterOptionsByField((prev) => ({ ...prev, _form: options }))
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error)
        if (!cancelled) {
          toast.error(`Failed to load form filter values: ${msg}`)
        }
      } finally {
        if (!cancelled) {
          setFormFilterLoadingByField((prev) => ({ ...prev, _form: false }))
        }
      }
    }

    void loadOptions()
    return () => {
      cancelled = true
    }
  }, [
    activeFilterColumnId,
    currentSlug,
    formFilterOptionsByField,
    formFilterLoadingByField,
    formTitleByUid,
  ])

  const getUidColumnOptionsFromTableData = useCallback(
    (fieldName: "_creator" | "_itemOwner"): string[] => {
      const rows = Array.isArray(tableData) ? tableData : []
      const resolver = fieldName === "_creator" ? resolveItemCreatorUid : resolveItemOwnerUid
      const unique = new Set<string>()
      rows.forEach((row) => {
        const u = resolver(row as Record<string, unknown>)
        if (u) unique.add(String(u).trim())
      })
      return Array.from(unique).sort((a, b) => {
        const la = getUserUidDisplayLabel(a) || a
        const lb = getUserUidDisplayLabel(b) || b
        return la.localeCompare(lb)
      })
    },
    [tableData]
  )

  useEffect(() => {
    const fieldName = activeFilterColumnId
    if (fieldName !== "_creator" && fieldName !== "_itemOwner") return
    if (!currentSlug) return
    if (uidColumnFilterOptionsByField[fieldName]) return
    if (uidColumnFilterLoadingByField[fieldName]) return

    let cancelled = false
    const loadOptions = async () => {
      try {
        setUidColumnFilterLoadingByField((prev) => ({ ...prev, [fieldName]: true }))
        const allRows: Record<string, unknown>[] = []
        let page = 1
        const limit = 100
        const resolver = fieldName === "_creator" ? resolveItemCreatorUid : resolveItemOwnerUid
        for (; ;) {
          const result = await fetchItemsAPI(currentSlug, { page, limit })
          const items = Array.isArray(result?.items) ? result.items : []
          allRows.push(...items)
          if (items.length < limit) break
          page += 1
        }

        const unique = new Set<string>()
        allRows.forEach((row) => {
          const u = resolver(row)
          if (u) unique.add(String(u).trim())
        })
        getUidColumnOptionsFromTableData(fieldName).forEach((u) => unique.add(u))
        const options = Array.from(unique).sort((a, b) => {
          const la = getUserUidDisplayLabel(a) || a
          const lb = getUserUidDisplayLabel(b) || b
          return la.localeCompare(lb)
        })
        if (!cancelled) {
          setUidColumnFilterOptionsByField((prev) => ({ ...prev, [fieldName]: options }))
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error)
        if (!cancelled) {
          toast.error(`Failed to load user filter values: ${msg}`)
        }
      } finally {
        if (!cancelled) {
          setUidColumnFilterLoadingByField((prev) => ({ ...prev, [fieldName]: false }))
        }
      }
    }

    void loadOptions()
    return () => {
      cancelled = true
    }
  }, [
    activeFilterColumnId,
    currentSlug,
    uidColumnFilterOptionsByField,
    uidColumnFilterLoadingByField,
    getUidColumnOptionsFromTableData,
  ])

  useEffect(() => {
    const fieldName = activeFilterColumnId
    if (!fieldName) return
    const fieldType = fieldTypeByName[fieldName]
    if (fieldType !== "relation" && fieldType !== "multiplerelation" && fieldType !== "systemrelation") return
    if (relationFilterOptionsByField[fieldName]) return
    if (relationFilterLoadingByField[fieldName]) return
    if (!currentSlug) return

    let cancelled = false
    const loadOptions = async () => {
      try {
        setRelationFilterLoadingByField((prev) => ({ ...prev, [fieldName]: true }))
        const allRows: Record<string, unknown>[] = []
        let page = 1
        const limit = 100
        for (; ;) {
          const result = await fetchItemsAPI(currentSlug, { page, limit })
          const items = Array.isArray(result?.items) ? result.items : []
          allRows.push(...items)
          if (items.length < limit) break
          page += 1
        }

        const unique = new Set<string>()
        allRows.forEach((row) => {
          getRelationFilterValuesForRow(row, fieldName).forEach((value) => unique.add(value))
        })

        getRelationFilterOptions(fieldName).forEach((value) => unique.add(value))
        const options = Array.from(unique).sort((a, b) => a.localeCompare(b))
        if (!cancelled) {
          setRelationFilterOptionsByField((prev) => ({ ...prev, [fieldName]: options }))
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error)
        if (!cancelled) {
          toast.error(`Failed to load relation filter values: ${msg}`)
        }
      } finally {
        if (!cancelled) {
          setRelationFilterLoadingByField((prev) => ({ ...prev, [fieldName]: false }))
        }
      }
    }

    loadOptions()
    return () => {
      cancelled = true
    }
  }, [
    activeFilterColumnId,
    currentSlug,
    fieldTypeByName,
    relationFilterLoadingByField,
    relationFilterOptionsByField,
    getRelationFilterValuesForRow,
    getRelationFilterOptions,
  ])

  const openCsvDialog = useCallback(
    (fieldName: string) => {
      const existingValues = csvFilters[fieldName] || []
      setCsvDialogFieldName(fieldName)
      setCsvDialogInput(existingValues.join(", "))
      setCsvDialogOpen(true)
    },
    [csvFilters]
  )

  const applyCsvDialog = useCallback(() => {
    if (!csvDialogFieldName) return
    const values = csvDialogInput
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
    setCsvFilters((prev) => {
      const next = { ...prev }
      if (values.length > 0) {
        next[csvDialogFieldName] = Array.from(new Set(values))
      } else {
        delete next[csvDialogFieldName]
      }
      return next
    })
    setCsvDialogOpen(false)
  }, [csvDialogFieldName, csvDialogInput])

  const clearCsvForField = useCallback((fieldName: string) => {
    setCsvFilters((prev) => {
      if (!prev[fieldName]) return prev
      const next = { ...prev }
      delete next[fieldName]
      return next
    })
  }, [])

  const clearAllColumnFilters = useCallback(() => {
    setTextFilters({})
    setNumberFilters({})
    setBooleanFilters({})
    setSelectFilters({})
    setCsvFilters({})
    setCreatedRange({})
    setUpdatedRange({})
  }, [])

  /** Change page and drop all filters so pagination is not fighting filter-driven refetches. */
  const goToPage = useCallback(
    (nextPage: number) => {
      prevCsvFilterSignatureRef.current = "[]"
      prevServerColumnFilterSignatureRef.current = "null"
      setTextFilters({})
      setNumberFilters({})
      setBooleanFilters({})
      setSelectFilters({})
      setCsvFilters({})
      setCreatedRange({})
      setUpdatedRange({})
      setDebouncedColumnFilterInput(EMPTY_COLUMN_FILTER_INPUT)
      setActiveFilterColumnId(null)
      setPage(Math.max(1, nextPage))
      setUpdateList(true)
    },
    [setPage, setUpdateList]
  )

  const applySearchColumnFilterFromNavigation = useCallback(
    (filter: SearchColumnFilter): boolean => {
      const blank: ColumnFilterStateInput = {
        text: {},
        number: {},
        boolean: {},
        select: {},
        created: {},
        updated: {},
      }
      const next = applySearchColumnFilterToInput(filter, fieldTypeByName, blank)
      if (!next) return false
      setTextFilters(next.text)
      setNumberFilters(next.number)
      setBooleanFilters(next.boolean)
      setSelectFilters(next.select)
      setCreatedRange(next.created)
      setUpdatedRange(next.updated)
      setCsvFilters({})
      setDebouncedColumnFilterInput(next)
      setPage(1)
      return true
    },
    [fieldTypeByName, setPage]
  )

  const applyDuplicateResultFilters = useCallback(
    (entries: DuplicateFilterEntry[]) => {
      if (!entries.length) return

      const nextText: Record<string, string> = {}
      const nextNumber: Record<string, { min?: string; max?: string; equals?: string }> = {}
      const nextBoolean: Record<string, "all" | "true" | "false"> = {}
      const nextSelect: Record<string, string[]> = {}
      const nextCsv: Record<string, string[]> = {}
      let nextCreated: { from?: string; to?: string } = {}
      let nextUpdated: { from?: string; to?: string } = {}
      let appliedAny = false
      let unsupported = false

      for (const { fieldName, comparableKey: key } of entries) {
        const type = String(fieldTypeByName[fieldName] || "").toLowerCase().trim()

        if (fieldName === "_creator" || fieldName === "_itemOwner" || fieldName === "_form") {
          nextSelect[fieldName] = key === "__empty__" ? [""] : [key]
          appliedAny = true
          continue
        }

        if (fieldName === "_created") {
          if (key === "__empty__") {
            unsupported = true
            continue
          }
          if (key.startsWith("ts:")) {
            const sec = parseInt(key.slice(3), 10)
            if (Number.isFinite(sec)) {
              const day = dateDayFromUnixSeconds(sec)
              nextCreated = { from: day, to: day }
              appliedAny = true
            } else unsupported = true
          } else unsupported = true
          continue
        }

        if (fieldName === "_updated") {
          if (key === "__empty__") {
            unsupported = true
            continue
          }
          if (key.startsWith("ts:")) {
            const sec = parseInt(key.slice(3), 10)
            if (Number.isFinite(sec)) {
              const day = dateDayFromUnixSeconds(sec)
              nextUpdated = { from: day, to: day }
              appliedAny = true
            } else unsupported = true
          } else unsupported = true
          continue
        }

        if (type === "checkbox" || type === "boolean" || type === "bool") {
          if (key === "bool:1") {
            nextBoolean[fieldName] = "true"
            appliedAny = true
          } else if (key === "bool:0") {
            nextBoolean[fieldName] = "false"
            appliedAny = true
          } else unsupported = true
          continue
        }

        if (type === "code") {
          if (key === "__empty__") {
            nextCsv[fieldName] = [""]
            appliedAny = true
            continue
          }
          if (key.startsWith("num:")) {
            nextNumber[fieldName] = { equals: key.slice(4), min: "", max: "" }
            appliedAny = true
            continue
          }
          if (key.startsWith("s:")) {
            nextCsv[fieldName] = [key.slice(2)]
            appliedAny = true
            continue
          }
          unsupported = true
          continue
        }

        if (isNumericFieldType(type)) {
          if (key === "__empty__") {
            unsupported = true
            continue
          }
          if (key.startsWith("num:")) {
            nextNumber[fieldName] = { equals: key.slice(4), min: "", max: "" }
            appliedAny = true
          } else unsupported = true
          continue
        }

        if (type === "select" || type === "dropdown") {
          if (key === "__empty__") nextSelect[fieldName] = [""]
          else if (key.startsWith("s:")) nextSelect[fieldName] = [key.slice(2)]
          else nextSelect[fieldName] = [key]
          appliedAny = true
          continue
        }

        if (type === "relation" || type === "multiplerelation" || type === "systemrelation") {
          if (key === "__empty__") {
            nextCsv[fieldName] = [""]
            appliedAny = true
            continue
          }
          if (key.startsWith("arr:")) {
            nextCsv[fieldName] = comparableArrKeyToCsvTokens(key)
            appliedAny = true
            continue
          }
          if (key.startsWith("uid:")) {
            nextSelect[fieldName] = [key.slice(4)]
            appliedAny = true
            continue
          }
          if (key.startsWith("s:")) {
            nextSelect[fieldName] = [key.slice(2)]
            appliedAny = true
            continue
          }
          if (key.startsWith("obj:")) {
            unsupported = true
            continue
          }
          nextSelect[fieldName] = [key]
          appliedAny = true
          continue
        }

        if (type === "richtext" || type === "textarea" || type === "text") {
          if (key === "__empty__") nextCsv[fieldName] = [""]
          else if (key.startsWith("s:")) nextCsv[fieldName] = [key.slice(2)]
          else nextCsv[fieldName] = [key]
          appliedAny = true
          continue
        }

        if (key === "__empty__") nextCsv[fieldName] = [""]
        else if (key.startsWith("s:")) nextCsv[fieldName] = [key.slice(2)]
        else nextCsv[fieldName] = [key]
        appliedAny = true
      }

      if (!appliedAny) {
        toast.error("Could not apply these values as column filters.")
        return
      }

      setTextFilters(nextText)
      setNumberFilters(nextNumber)
      setBooleanFilters(nextBoolean)
      setSelectFilters(nextSelect)
      setCsvFilters(nextCsv)
      setCreatedRange(nextCreated)
      setUpdatedRange(nextUpdated)
      setPage(1)
      setActiveFilterColumnId(null)
      if (unsupported) {
        toast.info("Some values could not be turned into column filters. Others were applied.")
      }
    },
    [fieldTypeByName, setPage]
  )

  const handleCsvDialogOpenChange = useCallback((open: boolean) => {
    setCsvDialogOpen(open)
    if (!open) {
      setCsvDialogFieldName(null)
      setCsvDialogInput("")
    }
  }, [])

  const renderCsvButton = useCallback(
    (fieldName: string) => {
      const hasCsvFilter = Array.isArray(csvFilters[fieldName]) && csvFilters[fieldName].length > 0
      return (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`h-6 px-2 text-[11px] ${hasCsvFilter ? "border-blue-500 text-blue-700 dark:text-blue-300" : ""}`}
          onClick={() => openCsvDialog(fieldName)}
        >
          CSV
        </Button>
      )
    },
    [csvFilters, openCsvDialog]
  )

  const renderFieldFilter = (field: { name?: string; type?: string; options?: unknown }) => {
    if (!field || !field.name) return null
    const fieldName = field.name
    const type = String(field.type || "").toLowerCase()

    if (fieldName === "_form") {
      const rows = Array.isArray(tableData) ? tableData : []
      const fromTable = buildFormFilterChoicesFromRows(rows as Record<string, unknown>[], formTitleByUid)
      const baseOptions = formFilterOptionsByField["_form"] ?? fromTable
      const options = baseOptions
        .map((o) => ({
          uid: o.uid,
          label: (formTitleByUid[o.uid] ?? "").trim() || o.label,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
      const isLoading = formFilterLoadingByField["_form"] === true
      const selected = selectFilters["_form"] || []
      if (!options.length && !isLoading) {
        return (
          <div className="pt-1 text-[11px] text-gray-500 dark:text-gray-400">
            No form submissions in this collection.
          </div>
        )
      }
      return (
        <div className="pt-1 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900">
          {isLoading && (
            <div className="mb-2 text-[11px] text-gray-500 dark:text-gray-400">
              Loading all values...
            </div>
          )}
          <div className="flex flex-col gap-1 text-[11px]">
            {options.map((opt) => {
              const checked = selected.includes(String(opt.uid))
              return (
                <label
                  key={opt.uid}
                  className="flex items-center gap-1 text-[11px] text-gray-700 dark:text-gray-300"
                  title={opt.uid}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => {
                      setSelectFilters((prev) => {
                        const prevSelected = prev["_form"] || []
                        if (value) {
                          if (prevSelected.includes(String(opt.uid))) return prev
                          return {
                            ...prev,
                            _form: [...prevSelected, String(opt.uid)],
                          }
                        }
                        return {
                          ...prev,
                          _form: prevSelected.filter((v) => v !== String(opt.uid)),
                        }
                      })
                    }}
                  />
                  <span className="min-w-0 break-all">{opt.label}</span>
                </label>
              )
            })}
          </div>
          <div className="mt-1 flex items-center gap-2">
            {renderCsvButton(fieldName)}
            <button
              type="button"
              className="text-[11px] underline text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              onClick={() => {
                setSelectFilters((prev) => ({
                  ...prev,
                  _form: [],
                }))
                clearCsvForField(fieldName)
                setActiveFilterColumnId(null)
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )
    }

    if (fieldName === "_creator" || fieldName === "_itemOwner") {
      const uidField = fieldName as "_creator" | "_itemOwner"
      const options =
        uidColumnFilterOptionsByField[fieldName] ?? getUidColumnOptionsFromTableData(uidField)
      const isLoading = uidColumnFilterLoadingByField[fieldName] === true
      const selected = selectFilters[fieldName] || []
      if (!options.length && !isLoading) {
        return (
          <div className="pt-1 text-[11px] text-gray-500 dark:text-gray-400">
            No user values in this collection.
          </div>
        )
      }
      return (
        <div className="pt-1 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900">
          {isLoading && (
            <div className="mb-2 text-[11px] text-gray-500 dark:text-gray-400">
              Loading all values...
            </div>
          )}
          <div className="flex flex-col gap-1 text-[11px]">
            {options.map((opt) => {
              const checked = selected.includes(String(opt))
              const optLabel = getUserUidDisplayLabel(opt) || opt
              return (
                <label
                  key={opt}
                  className="flex items-center gap-1 text-[11px] text-gray-700 dark:text-gray-300"
                  title={opt}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => {
                      setSelectFilters((prev) => {
                        const prevSelected = prev[fieldName] || []
                        if (value) {
                          if (prevSelected.includes(String(opt))) return prev
                          return {
                            ...prev,
                            [fieldName]: [...prevSelected, String(opt)],
                          }
                        }
                        return {
                          ...prev,
                          [fieldName]: prevSelected.filter((v) => v !== String(opt)),
                        }
                      })
                    }}
                  />
                  <span className="min-w-0 break-all">{optLabel}</span>
                </label>
              )
            })}
          </div>
          <div className="mt-1 flex items-center gap-2">
            {renderCsvButton(fieldName)}
            <button
              type="button"
              className="text-[11px] underline text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              onClick={() => {
                setSelectFilters((prev) => ({
                  ...prev,
                  [fieldName]: [],
                }))
                clearCsvForField(fieldName)
                setActiveFilterColumnId(null)
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )
    }

    if (type === "richtext" || type === "textarea" || type === "text") {
      return (
        <div className="pt-1">
          <ColumnFilterTextInput
            fieldName={fieldName}
            value={textFilters[fieldName] ?? ""}
            isOpen={activeFilterColumnId === fieldName}
            focusFieldRef={focusedFilterFieldRef}
            onChange={(next) =>
              setTextFilters((prev) => ({
                ...prev,
                [fieldName]: next,
              }))
            }
          />
          <div className="mt-1 flex items-center gap-2">
            {renderCsvButton(fieldName)}
            <button
              type="button"
              className="text-[11px] underline text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              onClick={() => {
                setTextFilters((prev) => ({
                  ...prev,
                  [fieldName]: "",
                }))
                clearCsvForField(fieldName)
                setActiveFilterColumnId(null)
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )
    }

    if (isNumericFieldType(type) || type === "code") {
      const currentFilter = numberFiltersRef.current[fieldName] || {}
      const updateNumberFilter = (key: "min" | "max" | "equals", value: string) => {
        setNumberFilters((prev) => ({
          ...prev,
          [fieldName]:
            key === "equals"
              ? { ...prev[fieldName], equals: value, min: "", max: "" }
              : { ...prev[fieldName], [key]: value, equals: "" },
        }))
      }
      return (
        <div className="pt-1 grid grid-cols-1 gap-1">
          <Input
            type="text"
            inputMode="decimal"
            value={currentFilter.equals ?? ""}
            onInput={(e) => updateNumberFilter("equals", (e.target as HTMLInputElement).value)}
            onChange={(e) => updateNumberFilter("equals", e.target.value)}
            placeholder="Equals"
            className="h-7 px-2 text-[11px] leading-tight bg-white dark:bg-white font-normal"
          />
          <Input
            type="text"
            inputMode="decimal"
            value={currentFilter.min ?? ""}
            onInput={(e) => updateNumberFilter("min", (e.target as HTMLInputElement).value)}
            onChange={(e) => updateNumberFilter("min", e.target.value)}
            placeholder="Min"
            className="h-7 px-2 text-[11px] leading-tight bg-white dark:bg-white font-normal"
          />
          <Input
            type="text"
            inputMode="decimal"
            value={currentFilter.max ?? ""}
            onInput={(e) => updateNumberFilter("max", (e.target as HTMLInputElement).value)}
            onChange={(e) => updateNumberFilter("max", e.target.value)}
            placeholder="Max"
            className="h-7 px-2 text-[11px] leading-tight bg-white dark:bg-white font-normal"
          />
          <div className="flex items-center gap-2 justify-self-start">
            {renderCsvButton(fieldName)}
            <button
              type="button"
              className="text-[11px] underline text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              onClick={() => {
                setNumberFilters((prev) => ({
                  ...prev,
                  [fieldName]: { equals: "", min: "", max: "" },
                }))
                clearCsvForField(fieldName)
                setActiveFilterColumnId(null)
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )
    }

    if (type === "checkbox" || type === "boolean" || type === "bool") {
      const mode = booleanFiltersRef.current[fieldName] ?? "all"
      return (
        <div className="pt-1">
          <Select
            value={mode}
            onValueChange={(v: "all" | "true" | "false") =>
              setBooleanFilters((prev) => ({
                ...prev,
                [fieldName]: v,
              }))
            }
          >
            <SelectTrigger className="h-8 text-[11px] bg-white dark:bg-white text-[11px]">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="true">Selected</SelectItem>
              <SelectItem value="false">Not selected</SelectItem>
            </SelectContent>
          </Select>
          <div className="mt-1 flex items-center gap-2">
            {renderCsvButton(fieldName)}
            <button
              type="button"
              className="text-[11px] underline text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              onClick={() => {
                setBooleanFilters((prev) => ({
                  ...prev,
                  [fieldName]: "all",
                }))
                clearCsvForField(fieldName)
                setActiveFilterColumnId(null)
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )
    }

    if (type === "select" || type === "dropdown") {
      const options = getSelectOptions(field)
      if (!options.length) return null
      const selected = selectFiltersRef.current[fieldName] || []
      return (
        <div className="pt-1 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900">
          <div className="flex flex-col gap-1 text-[11px]">
            {options.map((opt) => {
              const checked = selected.includes(String(opt))
              const display = formatDateFilterLabel(opt)
              return (
                <label
                  key={opt}
                  className="flex items-center gap-1 text-[11px] text-gray-700 dark:text-gray-300"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => {
                      setSelectFilters((prev) => {
                        const prevSelected = prev[fieldName] || []
                        if (value) {
                          if (prevSelected.includes(String(opt))) return prev
                          return {
                            ...prev,
                            [fieldName]: [...prevSelected, String(opt)],
                          }
                        }
                        return {
                          ...prev,
                          [fieldName]: prevSelected.filter((v) => v !== String(opt)),
                        }
                      })
                    }}
                  />
                  <span>{display || String(opt)}</span>
                </label>
              )
            })}
          </div>
          <div className="mt-1 flex items-center gap-2">
            {renderCsvButton(fieldName)}
            <button
              type="button"
              className="text-[11px] underline text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              onClick={() => {
                setSelectFilters((prev) => ({
                  ...prev,
                  [fieldName]: [],
                }))
                clearCsvForField(fieldName)
                setActiveFilterColumnId(null)
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )
    }

    if (type === "relation" || type === "multiplerelation" || type === "systemrelation") {
      const options = relationFilterOptionsByField[fieldName] ?? getRelationFilterOptions(fieldName)
      const isLoading = relationFilterLoadingByField[fieldName] === true
      if (!options.length && !isLoading) return null
      const selected = selectFiltersRef.current[fieldName] || []
      return (
        <div className="pt-1 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900">
          {isLoading && (
            <div className="mb-2 text-[11px] text-gray-500 dark:text-gray-400">Loading all relation values...</div>
          )}
          <div className="flex flex-col gap-1 text-[11px]">
            {options.map((opt) => {
              const checked = selected.includes(String(opt))
              const display = formatDateFilterLabel(opt)
              return (
                <label
                  key={opt}
                  className="flex items-center gap-1 text-[11px] text-gray-700 dark:text-gray-300"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => {
                      setSelectFilters((prev) => {
                        const prevSelected = prev[fieldName] || []
                        if (value) {
                          if (prevSelected.includes(String(opt))) return prev
                          return {
                            ...prev,
                            [fieldName]: [...prevSelected, String(opt)],
                          }
                        }
                        return {
                          ...prev,
                          [fieldName]: prevSelected.filter((v) => v !== String(opt)),
                        }
                      })
                    }}
                  />
                  <span>{display || String(opt)}</span>
                </label>
              )
            })}
          </div>
          <div className="mt-1 flex items-center gap-2">
            {renderCsvButton(fieldName)}
            <button
              type="button"
              className="text-[11px] underline text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              onClick={() => {
                setSelectFilters((prev) => ({
                  ...prev,
                  [fieldName]: [],
                }))
                clearCsvForField(fieldName)
                setActiveFilterColumnId(null)
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )
    }

    return null
  }

  const applyFilters = useCallback(
    (rows: unknown[]): unknown[] => {
      if (!rows || rows.length === 0) return rows

      const parseDateOnly = (value: unknown): Date | null => {
        const d = parseTableTimestamp(value)
        if (!d) return null
        return new Date(d.getFullYear(), d.getMonth(), d.getDate())
      }

      const parseDateInput = (value?: string): Date | null => {
        if (!value) return null
        const [year, month, day] = value.split("-").map((v) => parseInt(v, 10))
        if (!year || !month || !day) return null
        return new Date(year, month - 1, day)
      }

      const createdFrom = parseDateInput(createdRange.from)
      const createdTo = parseDateInput(createdRange.to)
      const updatedFrom = parseDateInput(updatedRange.from)
      const updatedTo = parseDateInput(updatedRange.to)

      const rowMatchesCsvTimestamp = (cellValue: unknown, allowedList: string[]): boolean => {
        const rowDate = parseTableTimestamp(cellValue)
        const variants = new Set<string>()
        if (cellValue !== null && cellValue !== undefined) {
          if (typeof cellValue === "object" && (cellValue as { seconds?: number }).seconds !== undefined) {
            const d = new Date((cellValue as { seconds: number }).seconds * 1000)
            variants.add(d.toISOString())
            variants.add(d.toISOString().slice(0, 10))
            variants.add(String((cellValue as { seconds: number }).seconds))
          } else {
            variants.add(String(cellValue).trim())
          }
        }
        if (rowDate && !isNaN(rowDate.getTime())) {
          variants.add(rowDate.toISOString())
          variants.add(rowDate.toISOString().slice(0, 10))
        }
        const hay = new Set([...variants].map((s) => s.toLowerCase()))
        return allowedList.some((raw) => {
          const t = raw.trim()
          if (!t) return false
          if (hay.has(t.toLowerCase())) return true
          const tokenDate = parseTableTimestamp(t)
          if (tokenDate && rowDate && !isNaN(tokenDate.getTime()) && !isNaN(rowDate.getTime())) {
            if (tokenDate.getTime() === rowDate.getTime()) return true
            return (
              tokenDate.getFullYear() === rowDate.getFullYear() &&
              tokenDate.getMonth() === rowDate.getMonth() &&
              tokenDate.getDate() === rowDate.getDate()
            )
          }
          return false
        })
      }

      return rows.filter((row) => {
        const r = row as Record<string, unknown>

        for (const [fieldName, termRaw] of Object.entries(textFilters)) {
          const term = termRaw.trim().toLowerCase()
          if (!term) continue
          const value = r[fieldName]
          let text = ""
          if (value == null) {
            text = ""
          } else if (typeof value === "string") {
            text = value.toLowerCase()
          } else if (typeof value === "number" || typeof value === "boolean") {
            text = String(value).toLowerCase()
          } else if (Array.isArray(value)) {
            text = value
              .map((entry) => (entry == null ? "" : String(entry)))
              .join(" ")
              .toLowerCase()
          } else {
            text = String(value).toLowerCase()
          }
          if (!text.includes(term)) {
            return false
          }
        }

        for (const [fieldName, range] of Object.entries(numberFilters)) {
          const hasEquals = range.equals && range.equals.trim() !== ""
          const hasMin = range.min && range.min.trim() !== ""
          const hasMax = range.max && range.max.trim() !== ""
          if (!hasEquals && !hasMin && !hasMax) continue
          const raw = r[fieldName]
          const n = typeof raw === "number" ? raw : parseFloat(String(raw))
          if (!Number.isFinite(n)) {
            return false
          }
          if (hasEquals) {
            const eqVal = parseFloat(range.equals!)
            if (Number.isFinite(eqVal) && n !== eqVal) {
              return false
            }
            continue
          }
          if (hasMin) {
            const minVal = parseFloat(range.min!)
            if (Number.isFinite(minVal) && n < minVal) {
              return false
            }
          }
          if (hasMax) {
            const maxVal = parseFloat(range.max!)
            if (Number.isFinite(maxVal) && n > maxVal) {
              return false
            }
          }
        }

        for (const [fieldName, mode] of Object.entries(booleanFilters)) {
          if (mode === "all") continue
          const normalized = normalizeBoolean(r[fieldName])
          if (normalized === null) return false
          if (mode === "true" && !normalized) return false
          if (mode === "false" && normalized) return false
        }

        for (const [fieldName, selectedValues] of Object.entries(selectFilters)) {
          if (!selectedValues || selectedValues.length === 0) continue
          if (fieldName === "_form") {
            const cell = getRowFormUid(r)
            if (!selectedValues.includes(cell)) return false
            continue
          }
          if (fieldName === "_creator" || fieldName === "_itemOwner") {
            const uid =
              fieldName === "_creator"
                ? resolveItemCreatorUid(r)
                : resolveItemOwnerUid(r)
            const cell = uid == null || String(uid).trim() === "" ? "" : String(uid).trim()
            if (!selectedValues.includes(cell)) return false
            continue
          }
          const fieldType = fieldTypeByName[fieldName]
          if (fieldType === "relation" || fieldType === "multiplerelation" || fieldType === "systemrelation") {
            const relationValues = getRelationFilterValuesForRow(r, fieldName)
            const hasAny = relationValues.some((value) => selectedValues.includes(value))
            if (!hasAny) return false
            continue
          }
          const raw = r[fieldName]
          if (Array.isArray(raw)) {
            const hasAny = raw.some((v) => selectedValues.includes(String(v)))
            if (!hasAny) return false
          } else {
            if (!selectedValues.includes(String(raw))) return false
          }
        }

        if (createdFrom || createdTo) {
          const createdValue = r.createdAt ?? r._created
          const createdDateOnly = parseDateOnly(createdValue)
          if (!createdDateOnly) return false
          if (createdFrom && createdDateOnly < createdFrom) return false
          if (createdTo && createdDateOnly > createdTo) return false
        }

        if (updatedFrom || updatedTo) {
          const updatedValue = r.updatedAt ?? r._updated
          const updatedDateOnly = parseDateOnly(updatedValue)
          if (!updatedDateOnly) return false
          if (updatedFrom && updatedDateOnly < updatedFrom) return false
          if (updatedTo && updatedDateOnly > updatedTo) return false
        }

        for (const [fieldName, allowedValues] of Object.entries(normalizedCsvFilters)) {
          if (!allowedValues.length) continue
          if (fieldName === "_form") {
            const cell = getRowFormUid(r)
            if (!allowedValues.includes(cell)) return false
            continue
          }
          if (fieldName === "_creator" || fieldName === "_itemOwner") {
            const uid =
              fieldName === "_creator"
                ? resolveItemCreatorUid(r)
                : resolveItemOwnerUid(r)
            const cell = uid == null ? "" : String(uid).trim()
            if (!allowedValues.includes(cell)) return false
            continue
          }
          if (fieldName === "_created") {
            const createdValue = r.createdAt ?? r._created
            if (!rowMatchesCsvTimestamp(createdValue, allowedValues)) return false
            continue
          }
          if (fieldName === "_updated") {
            const updatedValue = r.updatedAt ?? r._updated
            if (!rowMatchesCsvTimestamp(updatedValue, allowedValues)) return false
            continue
          }
          const fieldType = fieldTypeByName[fieldName]
          if (
            fieldType === "relation" ||
            fieldType === "multiplerelation" ||
            fieldType === "systemrelation"
          ) {
            const relationValues = getRelationFilterValuesForRow(r, fieldName)
            const hasAny = relationValues.some((value) => allowedValues.includes(value))
            if (!hasAny) return false
            continue
          }
          const raw = r[fieldName]
          if (Array.isArray(raw)) {
            const hasAny = raw.some((v) => allowedValues.includes(String(v).trim()))
            if (!hasAny) return false
          } else {
            const cell = raw == null ? "" : String(raw).trim()
            if (!allowedValues.includes(cell)) return false
          }
        }

        return true
      })
    },
    [
      textFilters,
      numberFilters,
      booleanFilters,
      selectFilters,
      createdRange,
      updatedRange,
      normalizedCsvFilters,
      fieldTypeByName,
      getRelationFilterValuesForRow,
    ]
  )

  const filteredRows = useMemo(() => {
    return Array.isArray(tableData) ? tableData : []
  }, [tableData])

  /** Stable snapshot of all client-side filter state; use to reset row selection when filters change. */
  const filtersSignature = useMemo(
    () =>
      JSON.stringify({
        text: textFilters,
        number: numberFilters,
        boolean: booleanFilters,
        select: selectFilters,
        csv: normalizedCsvFilters,
        created: createdRange,
        updated: updatedRange,
      }),
    [
      textFilters,
      numberFilters,
      booleanFilters,
      selectFilters,
      normalizedCsvFilters,
      createdRange,
      updatedRange,
    ]
  )

  return {
    activeFilterColumnId,
    setActiveFilterColumnId,
    selectFilters,
    csvFilters,
    createdRange,
    setCreatedRange,
    updatedRange,
    setUpdatedRange,
    createdRangeRef,
    updatedRangeRef,
    normalizedCsvFilters,
    serverColumnFilters,
    hasActiveFilters,
    appliedFilterCount,
    filteredRows,
    applyFilters,
    isFieldFilterApplied,
    renderFieldFilter,
    renderCsvButton,
    clearCsvForField,
    clearAllColumnFilters,
    goToPage,
    applySearchColumnFilterFromNavigation,
    applyDuplicateResultFilters,
    csvDialogOpen,
    csvDialogFieldName,
    csvDialogInput,
    setCsvDialogInput,
    handleCsvDialogOpenChange,
    applyCsvDialog,
    filtersSignature,
  }
}
